import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, COMPANY } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkCls = ({ isActive }) =>
    `relative font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:text-brand-ice ${
      isActive ? "text-brand-ice" : "text-white/75"
    }`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "border-b border-brand-cyan/15 bg-brand-teal/85 backdrop-blur-md"
            : "border-b border-transparent bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-5 sm:px-8 lg:h-20">
          <Link to="/" data-testid="nav-home-logo" aria-label="ECFCOSTRUZIONI — Home">
            <Logo compact />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop">
            {NAV_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}-link`}>
                {({ isActive }) => (
                  <span className={isActive ? "text-brand-ice" : ""}>
                    {l.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-px bg-brand-cyan transition-all duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a
              href={COMPANY.phoneHref}
              data-testid="nav-phone-link"
              className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/85 transition hover:border-brand-cyan hover:text-brand-ice"
            >
              <Phone className="h-3.5 w-3.5" />
              {COMPANY.phone}
            </a>
            <Link
              to="/contatti"
              data-testid="nav-quote-button"
              className="bg-brand-cyan px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-teal transition hover:bg-brand-ice"
            >
              Preventivo
            </Link>
          </div>

          <button
            className="flex h-10 w-10 items-center justify-center text-white lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-brand-teal/97 pt-28 pb-10 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-2 px-6">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    className={({ isActive }) =>
                      `block border-b border-white/10 py-4 font-display text-4xl font-bold uppercase tracking-tight ${
                        isActive ? "text-brand-ice" : "text-white"
                      }`
                    }
                    data-testid={`nav-mobile-${l.to === "/" ? "home" : l.to.slice(1)}-link`}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="px-6 font-mono text-xs uppercase tracking-[0.2em] text-white/60"
            >
              <a href={COMPANY.phoneHref} className="block py-2 text-brand-ice" data-testid="nav-mobile-phone-link">
                {COMPANY.phone}
              </a>
              <p className="py-2">{COMPANY.address}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
